/*
 * Copyright (c) 2016 Macrofocus GmbH. All Rights Reserved.
 */
@file:OptIn(ExperimentalTime::class)

package com.macrofocus.high_d.axis

import com.macrofocus.common.filter.MutableFilter
import com.macrofocus.common.interval.MutableBoundedInterval
import com.macrofocus.common.interval.SimpleBoundedInterval
import com.macrofocus.common.properties.MutableProperty
import com.macrofocus.common.properties.SimpleProperty
import com.macrofocus.common.selection.Selection
import com.macrofocus.common.selection.SingleSelection
import com.macrofocus.high_d.axis.AxisModel.Companion.DATA
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.mkui.component.CPFactory
import org.molap.dataframe.ColumnStatistics
import org.molap.dataframe.DataFrame
import org.molap.dataframe.DataFrameHelper
import org.molap.series.Series
import org.molap.subset.OrdinalDimension
import org.molap.subset.SubsetDataFrame
import kotlin.math.sqrt
import kotlin.reflect.KClass
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

class DateAxisModel<Row, C>(
    factory: CPFactory,
    dataFrame: DataFrame<Row, C, *>,
    annotationDataFrame: DataFrame<*, C, *>?,
    subsetDataFrame: SubsetDataFrame<Row, C, *>?,
    column: C,
    probing: SingleSelection<Row>,
    selection: Selection<Row>,
    filter: MutableFilter<Row>
) : AbstractAxisModel<Row, C>() {
    protected val dataFrame: DataFrame<Row, C, *>
    override val column: C
    protected val series: Array<Series<Row,C>?>
    override val dimension: OrdinalDimension<Row,C,*>?
    override val probing: SingleSelection<Row>
    override val selection: Selection<Row>
    override val filter: MutableFilter<Row>
    override val interval: MutableBoundedInterval by lazy {
        val interval = createBoundedInterval()
        addIntervalListener(interval)
        interval
    }

    override val buttonModel: MutableProperty<Boolean> = SimpleProperty<Boolean>(false)
    private var min = Double.MAX_VALUE
    private var max = -Double.MAX_VALUE
    override val mean: Double
    override val stdDev: Double
    override val binCount: MutableProperty<Int> = SimpleProperty<Int>(0)
    override val binType: MutableProperty<BinType> = SimpleProperty<BinType>(BinType.Fixed)
    protected fun createBoundedInterval(): MutableBoundedInterval {
        val interval: MutableBoundedInterval = SimpleBoundedInterval(min, max - min, min, max)
        interval.minimumExtent = -(max - min)
        registerInterval(interval)
        return interval
    }

    override val isNumerical: Boolean
        get() = true
    override val name: String
        get() = dataFrame.getColumnName(column)!!
    override val type: KClass<Any>
        get() = dataFrame.getColumnClass(column) as KClass<Any>

    override operator fun iterator(): Iterator<Row> {
        return dataFrame.rows().iterator()
    }

    override fun getValue(row: Row): Number? {
        val value: Any? = series[DATA]?.get(row)
        return if (value != null) {
            if (value is Instant) {
                val date: Instant = value
                date.toEpochMilliseconds()
            } else if (value is Number) {
                value
            } else {
                null
            }
        } else {
            null
        }
    }

    override fun getValue(layer: Int, row: Row): Number? {
        val value: Any? = series[layer]?.get(row)
        return if (value != null) {
            if (value is Instant) {
                val date: Instant = value
                date.toEpochMilliseconds()
            } else if (value is Number) {
                value
            } else {
                null
            }
        } else {
            null
        }
    }

    override val formattedMinimum: String
        get() = format!!.formatHtml(minimum, false)!!
    override val formattedMaximum: String
        get() = format!!.formatHtml(maximum, false)!!

    override fun getFormattedValue(row: Row): String {
        return getFormattedValue(DATA, row)
    }

    override fun getFormattedValue(layer: Int, row: Row): String {
        val value: Any? = series[layer]?.get(row)
        return if (value != null) {
            if (value is Instant) {
                if (format != null) {
                    format!!.formatHtml(value, true)!!
                } else {
//                    return NumberFormat.getNumberInstance().format(value);
                    value.toString()
                }
            } else if (value is Number) {
                format!!.formatHtml(Instant.fromEpochMilliseconds(value.toLong()), false)!!
            } else {
                value.toString()
            }
        } else {
            ""
        }
    }

    override fun getFormattedValue(value: Double): String {
        return if (format != null) {
            format!!.formatHtml(Instant.fromEpochMilliseconds(value.toLong()), true)!!
        } else {
            format!!.formatHtml(Instant.fromEpochMilliseconds(value.toLong()), false)!!
        }
    }

    override fun getOriginalFloorValue(value: Double): Any {
        return value
    }

    override fun getOriginalCeilValue(value: Double): Any {
        return value
    }

    override var minimum: Double
        get() = min
        set(min) {
            if (this.min != min) {
                this.min = min
                interval.setMinMax(min, max)
                interval.minimumExtent = -(max - min)
                fireAxisChanged()
            }
        }
    override var maximum: Double
        get() = max
        set(max) {
            if (this.max != max) {
                this.max = max
                interval.setMinMax(min, max)
                interval.minimumExtent = -(max - min)
                fireAxisChanged()
            }
        }
    override val isDegenerate: Boolean
        get() = max == min

    override fun setMinMax(min: Double, max: Double) {
        if (this.max != max || this.min != min) {
            this.min = min
            this.max = max
            interval.setMinMax(min, max)
            interval.minimumExtent = if (max >= min) 0.0 else max - min
            interval.maximumExtent = if (max >= min) max - min else 0.0
            fireAxisChanged()
        }
    }

    override val columnStatistics: ColumnStatistics
        get() = DataFrameHelper.getColumnStatistics(dataFrame as DataFrame<Row, C, Any?>, column)
    override val rowCount: Int
        get() = dataFrame.rowCount

    override fun getRow(row: Int): Row {
        return dataFrame.getRowKey(row)
    }

    override fun toString(): String {
        return name
    }

    init {
        this.dataFrame = dataFrame
        this.column = column
        dimension = if (subsetDataFrame != null) subsetDataFrame.getOrdinalDimension(column) else null
        series = arrayOfNulls<Series<Row,C>>(2)
        series[0] = dataFrame.getColumn(column) as Series<Row, C>
        series[1] = if (annotationDataFrame != null) annotationDataFrame.getColumn(column) as Series<Row,C> else null
        require(series[0] != null) { "Column $column could not be found" }
        require(annotationDataFrame == null || series[1] != null) { "Annotated column $column could not be found" }
        this.probing = probing
        this.selection = selection
        this.filter = filter
        var dateTime = false
        for (row in dataFrame.rows()) {
            val value: Any? = series[DATA]?.get(row)
            if (value != null) {
                val date: Instant?
                if (value is Instant) {
                    date = value as Instant
                } else if (value is Number) {
                    date = Instant.fromEpochMilliseconds(value.toLong())
                } else {
                    date = null
                }
                val localDateTime = date?.toLocalDateTime(TimeZone.UTC)
                if (localDateTime != null && localDateTime.hour != 0 || localDateTime?.minute != 0 || localDateTime?.second != 0) {
                    dateTime = true
                    break
                }
            }
        }
        var min = Double.MAX_VALUE
        var max = -Double.MAX_VALUE
        var sum = 0.0
        var count = 0
        var uncorrectedSumOfSquares = 0.0
        for (r in dataFrame.rows()) {
            val value = getValue(r)
            if (value != null) {
                val v = value.toDouble()
                if (v < min) {
                    min = v
                }
                if (v > max) {
                    max = v
                }
                sum += v
                uncorrectedSumOfSquares += v * v
                count++
            }
        }
        this.min = min
        this.max = max
        mean = sum / count
        stdDev = sqrt(uncorrectedSumOfSquares / count - mean * mean)
        if (dateTime) {
            format = AutoDateTimeFormat()
        } else {
            format = AutoDateTimeFormat()
        }
    }
}